import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Typography, Form, DatePicker, Select, Button, Space, Table, Tag, Segmented, Drawer, Alert, Tooltip,
} from 'antd';
import { DownloadOutlined, TeamOutlined, TagOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';
import { formatTS } from '../utils/time';

// Coaching Coach Stats: what each coach does. Every view is per coach.
//   By Coach        Draft, Active, Completed, Total, Participants,
//                   Participants Completed, Missed Due Date
//   By Request Type coach x request type   } Active and Completed sessions only
//   By Area         coach x area           } (drafts left out); split on hover
// Sessions created in the date range (Canceled left out), on the caller's
// assigned campaigns. Counts open the sessions behind them in a side panel.
// Missed Due Date counts Active and Completed sessions only (the server never
// counts a draft late).

const { RangePicker } = DatePicker;
const SESSION_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed' };
const SESSION_COLOR = { draft: 'default', active: 'blue', completed: 'green' };
const NONE = { RequestType: '(No request type)', Area: '(No area)' };

const byName = (a, b) => (a || '').localeCompare(b || '');
// Sessions that have gone out: what the grids and Missed Due Date count.
const live = (s) => s.Status === 'active' || s.Status === 'completed';

export default function CoachingCoachStatsPage() {
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  const canView = currentUser?.Role !== 'agent' && (currentUser?.CanEditCoaching ?? 0) >= 1;
  const [form] = Form.useForm();

  const [view, setView]       = useState('coach');   // coach | request_type | area
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ request_types: [], campaigns: [] });
  const [areas, setAreas]     = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [panel, setPanel]     = useState(null);       // { title, sessions }

  useEffect(() => {
    if (!canView) return;
    api.get('/api/coaching/agent-stats/filters').then((r) => setFilters(r.data || { request_types: [], campaigns: [] })).catch(() => {});
    api.get('/api/coaching/areas').then((r) => setAreas(r.data || [])).catch(() => {});
    api.get('/api/coaching/coaches').then((r) => setCoaches(r.data || [])).catch(() => {});
  }, [canView]);

  const load = useCallback(async () => {
    const v = form.getFieldsValue();
    const p = {};
    if (v.range?.[0]) p.date_from = v.range[0].format('YYYY-MM-DD');
    if (v.range?.[1]) p.date_to   = v.range[1].format('YYYY-MM-DD');
    if (v.campaigns?.length) p.campaign_id     = v.campaigns.join(',');
    if (v.types?.length)     p.request_type_id = v.types.join(',');
    if (v.areas?.length)     p.area_id         = v.areas.join(',');
    if (v.coaches?.length)   p.coach_user_id   = v.coaches.join(',');
    setLoading(true);
    try {
      const res = await api.get('/api/coaching/coach-stats', { params: p });
      setRows(res.data || []);
    } catch { setRows([]); } finally { setLoading(false); }
  }, [form]);

  useEffect(() => {
    form.setFieldsValue({ range: [dayjs().startOf('month'), dayjs()] });
    if (canView) load();
  }, []); // eslint-disable-line

  // -- By Coach -----------------------------------------------------------------
  const byCoach = useMemo(() => {
    const m = {};
    rows.forEach((s) => {
      const key = s.CoachUserID ?? 'none';
      const c = (m[key] ||= { key, CoachName: s.CoachName, sessions: [], Draft: 0, Active: 0, Completed: 0,
                               Total: 0, Participants: 0, ParticipantsCompleted: 0, MissedDueDate: 0 });
      c.sessions.push(s);
      c.Total += 1;
      if (s.Status === 'draft') c.Draft += 1;
      else if (s.Status === 'active') c.Active += 1;
      else if (s.Status === 'completed') c.Completed += 1;
      c.Participants += s.Participants;
      c.ParticipantsCompleted += s.ParticipantsCompleted;
      c.MissedDueDate += s.MissedDueDate;
    });
    return Object.values(m).sort((a, b) => byName(a.CoachName, b.CoachName));
  }, [rows]);

  const open = (title, sessions) => sessions.length && setPanel({ title, sessions });
  const countCell = (value, title, sessions, danger) => (value
    ? <Button type="link" size="small" danger={danger} style={{ padding: 0 }} onClick={() => open(title, sessions)}>
        {danger ? <strong>{value}</strong> : value}
      </Button>
    : <span style={{ color: '#bfbfbf' }}>0</span>);

  const COACH_METRICS = [
    { key: 'Draft',         title: 'Draft',          pick: (s) => s.Status === 'draft' },
    { key: 'Active',        title: 'Active',         pick: (s) => s.Status === 'active' },
    { key: 'Completed',     title: 'Completed',      pick: (s) => s.Status === 'completed' },
    { key: 'Total',         title: 'Total Sessions', pick: () => true },
  ];
  const coachColumns = [
    { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', fixed: 'left', width: 200, sorter: (a, b) => byName(a.CoachName, b.CoachName) },
    ...COACH_METRICS.map((m) => ({
      title: m.title, dataIndex: m.key, key: m.key, width: 120, align: 'right', sorter: (a, b) => a[m.key] - b[m.key],
      render: (v, c) => countCell(v, `${c.CoachName}: ${m.title}`, c.sessions.filter(m.pick)),
    })),
    { title: 'Participants', dataIndex: 'Participants', key: 'Participants', width: 120, align: 'right',
      sorter: (a, b) => a.Participants - b.Participants },
    { title: 'Participants Completed', dataIndex: 'ParticipantsCompleted', key: 'ParticipantsCompleted', width: 170, align: 'right',
      sorter: (a, b) => a.ParticipantsCompleted - b.ParticipantsCompleted },
    { title: 'Missed Due Date', dataIndex: 'MissedDueDate', key: 'MissedDueDate', width: 140, align: 'right',
      sorter: (a, b) => a.MissedDueDate - b.MissedDueDate,
      render: (v, c) => countCell(v, `${c.CoachName}: Missed Due Date`, c.sessions.filter((s) => s.MissedDueDate > 0), true) },
  ];
  const coachTotals = byCoach.reduce((t, c) => {
    ['Draft', 'Active', 'Completed', 'Total', 'Participants', 'ParticipantsCompleted', 'MissedDueDate']
      .forEach((k) => { t[k] = (t[k] || 0) + c[k]; });
    return t;
  }, {});

  // -- Coach x Request Type / Area -------------------------------------------------
  // Both grids count Active and Completed sessions only (drafts have not gone
  // out yet), which the table title says.
  const dim = view === 'area' ? 'Area' : 'RequestType';
  const grid = useMemo(() => {
    const cols = [...new Set(rows.filter(live).map((s) => s[dim] || NONE[dim]))].sort(byName);
    const data = byCoach.map((c) => {
      const counted = c.sessions.filter(live);
      const r = { key: c.key, CoachName: c.CoachName, Total: counted.length, cells: {} };
      counted.forEach((s) => {
        const k = s[dim] || NONE[dim];
        (r.cells[k] ||= []).push(s);
      });
      return r;
    });
    return { cols, data };
  }, [rows, byCoach, dim]);

  const gridColumns = [
    { title: 'Coach', dataIndex: 'CoachName', key: 'CoachName', fixed: 'left', width: 200, sorter: (a, b) => byName(a.CoachName, b.CoachName) },
    ...grid.cols.map((col) => ({
      title: col, key: col, width: 130, align: 'right',
      sorter: (a, b) => (a.cells[col]?.length || 0) - (b.cells[col]?.length || 0),
      render: (_, r) => {
        const list = r.cells[col] || [];
        if (!list.length) return <span style={{ color: '#d9d9d9' }}>-</span>;
        const done = list.filter((s) => s.Status === 'completed').length;
        const pend = list.filter((s) => s.Status === 'active').length;
        return (
          <Tooltip title={`${done} completed, ${pend} active`}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => open(`${r.CoachName}: ${col}`, list)}>{list.length}</Button>
          </Tooltip>
        );
      },
    })),
    { title: 'Total', dataIndex: 'Total', key: 'Total', width: 90, align: 'right', fixed: 'right',
      sorter: (a, b) => a.Total - b.Total, render: (v) => <strong>{v}</strong> },
  ];

  // -- export -----------------------------------------------------------------------
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byCoach.map((c) => ({
      'Coach': c.CoachName, 'Draft': c.Draft, 'Active': c.Active, 'Completed': c.Completed, 'Total Sessions': c.Total,
      'Participants': c.Participants, 'Participants Completed': c.ParticipantsCompleted, 'Missed Due Date': c.MissedDueDate,
    }))), 'By Coach');
    // Active and Completed only, as on screen.
    const gridSheet = (field, name) => {
      const cols = [...new Set(rows.filter(live).map((s) => s[field] || NONE[field]))].sort(byName);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(byCoach.map((c) => {
        const counted = c.sessions.filter(live);
        const out = { Coach: c.CoachName };
        cols.forEach((col) => { out[col] = counted.filter((s) => (s[field] || NONE[field]) === col).length; });
        out.Total = counted.length;
        return out;
      })), name);
    };
    gridSheet('RequestType', 'By Request Type');
    gridSheet('Area', 'By Area');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows.map((s) => ({
      'Coach': s.CoachName, 'Session ID': s.ID, 'Session': s.Title, 'Status': SESSION_LABEL[s.Status] ?? s.Status,
      'Campaign': s.Campaign ?? '', 'Request Type': s.RequestType ?? '', 'Area': s.Area ?? '',
      'Created': formatTS(s.CreatedAt), 'Due Date': s.DueDate ?? '', 'Participants': s.Participants,
      'Participants Completed': s.ParticipantsCompleted, 'Missed Due Date': s.MissedDueDate,
    }))), 'Sessions');
    XLSX.writeFile(wb, 'coaching-coach-stats.xlsx');
  };

  if (!canView) return <Alert type="warning" showIcon message="You do not have access to this page." />;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Coaching Coach Stats</Typography.Title>
        <Segmented value={view} onChange={setView} options={[
          { label: 'By Coach',        value: 'coach',        icon: <TeamOutlined /> },
          { label: 'By Request Type', value: 'request_type', icon: <TagOutlined /> },
          { label: 'By Area',         value: 'area',         icon: <AppstoreOutlined /> },
        ]} />
      </div>

      <Form form={form} layout="inline" onFinish={load} style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Form.Item name="range" label="Created">
          <RangePicker style={{ width: 260 }} allowClear={false} />
        </Form.Item>
        <Form.Item name="campaigns" label="Campaign">
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="All campaigns" style={{ width: 220 }}
            optionFilterProp="label" options={filters.campaigns.map((c) => ({ value: c.ID, label: c.Name }))} />
        </Form.Item>
        <Form.Item name="types" label="Request Type">
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="All request types" style={{ width: 220 }}
            optionFilterProp="label" options={filters.request_types.map((t) => ({ value: t.ID, label: t.Name }))} />
        </Form.Item>
        <Form.Item name="areas" label="Area">
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="All areas" style={{ width: 200 }}
            optionFilterProp="label" options={areas.map((a) => ({ value: a.ID, label: a.Name }))} />
        </Form.Item>
        <Form.Item name="coaches" label="Coach">
          <Select mode="multiple" allowClear maxTagCount="responsive" placeholder="All coaches" style={{ width: 220 }}
            optionFilterProp="label" options={coaches.map((c) => ({ value: c.ID, label: c.DisplayName }))} />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>Filter</Button>
            <Button icon={<DownloadOutlined />} onClick={exportToExcel} disabled={!rows.length}>Export</Button>
          </Space>
        </Form.Item>
      </Form>

      {view === 'coach' ? (
        <Table
          rowKey="key"
          dataSource={byCoach}
          columns={coachColumns}
          loading={loading}
          size="small"
          bordered
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No coaching sessions for these filters.' }}
          summary={() => (byCoach.length ? (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                {['Draft', 'Active', 'Completed', 'Total', 'Participants', 'ParticipantsCompleted', 'MissedDueDate'].map((k, i) => (
                  <Table.Summary.Cell key={k} index={i + 1} align="right"><strong>{coachTotals[k] || 0}</strong></Table.Summary.Cell>
                ))}
              </Table.Summary.Row>
            </Table.Summary>
          ) : null)}
        />
      ) : (
        <>
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          {view === 'area' ? 'Sessions by Area' : 'Sessions by Request Type'} (Active and Completed only)
        </Typography.Title>
        <Table
          rowKey="key"
          dataSource={grid.data}
          columns={gridColumns}
          loading={loading}
          size="small"
          bordered
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No coaching sessions for these filters.' }}
        />
        </>
      )}

      <Drawer title={panel?.title} open={!!panel} onClose={() => setPanel(null)} width={1000} destroyOnClose>
        <Table
          rowKey="ID"
          dataSource={panel?.sessions || []}
          size="small"
          pagination={{ defaultPageSize: 25, hideOnSinglePage: true }}
          scroll={{ x: 'max-content' }}
          onRow={(s) => ({ onClick: () => navigate(`/coaching/sessions/${s.ID}`), style: { cursor: 'pointer' } })}
          columns={[
            { title: 'ID', dataIndex: 'ID', key: 'ID', width: 70 },
            { title: 'Session', dataIndex: 'Title', key: 'Title', width: 240, ellipsis: true },
            { title: 'Status', dataIndex: 'Status', key: 'Status', width: 100,
              render: (v) => <Tag color={SESSION_COLOR[v]}>{SESSION_LABEL[v] ?? v}</Tag> },
            { title: 'Campaign', dataIndex: 'Campaign', key: 'Campaign', width: 120, render: (v) => v || '—' },
            { title: 'Request Type', dataIndex: 'RequestType', key: 'RequestType', width: 150, render: (v) => v || '—' },
            { title: 'Area', dataIndex: 'Area', key: 'Area', width: 130, render: (v) => v || '—' },
            { title: 'Created', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 160, render: (v) => formatTS(v),
              sorter: (a, b) => String(a.CreatedAt).localeCompare(String(b.CreatedAt)), defaultSortOrder: 'descend' },
            { title: 'Due Date', dataIndex: 'DueDate', key: 'DueDate', width: 100, render: (v) => v || '—' },
            { title: 'Participants', key: 'P', width: 110, align: 'right',
              render: (_, s) => `${s.ParticipantsCompleted} / ${s.Participants}` },
            { title: 'Missed Due', dataIndex: 'MissedDueDate', key: 'MissedDueDate', width: 100, align: 'right',
              render: (v) => (v ? <Typography.Text type="danger" strong>{v}</Typography.Text> : 0) },
          ]}
        />
      </Drawer>
    </div>
  );
}
